/**
 * ThemeLaunchBanner — Code-dashboard "New look available" banner.
 * Shown while `customer.modern_theme_toggle` is ON to Code-theme customers
 * who haven't voted or dismissed it. "Try Modern" switches theme in place,
 * where the feedback poll (if `customer.theme_feedback_poll` is ON) awaits.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useFlag } from '../context/FeatureFlagsContext';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const keyFor = (uid) => `theme_launch_banner_dismissed:${uid}`;

export function ThemeLaunchBanner() {
  const { user } = useAuth();
  const { isModern, setTheme } = useTheme();
  const toggleOn = useFlag('customer.modern_theme_toggle', false);
  const pollOn = useFlag('customer.theme_feedback_poll', false);
  const [dismissed, setDismissed] = useState(() => !!(user?.id && localStorage.getItem(keyFor(user.id))));
  const [switching, setSwitching] = useState(false);
  const eligible = !!(toggleOn && user?.role === 'customer' && !isModern && !dismissed);

  const status = useQuery({
    queryKey: ['customer', 'theme-feedback-status'],
    queryFn: async () => (await axios.get(`${API_URL}/api/users/me/theme-feedback`, { withCredentials: true })).data,
    enabled: eligible,
    staleTime: Infinity,
  });

  if (!eligible || status.isLoading || status.data?.submitted) return null;

  const dismiss = () => { localStorage.setItem(keyFor(user.id), '1'); setDismissed(true); };
  const tryModern = async () => { setSwitching(true); await setTheme('modern'); window.scrollTo(0, 0); };

  return (
    <div className="relative border border-[#39FF14] bg-[#0C0612] p-4 mb-6 shadow-[0_0_24px_rgba(57,255,20,0.15)]" data-testid="theme-launch-banner">
      <button type="button" onClick={dismiss} className="absolute top-2 right-2 p-1.5 text-[#6B8E73] hover:text-[#FF3B30]" aria-label="Dismiss" data-testid="theme-launch-dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <Sparkles className="w-5 h-5 text-[#39FF14] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-mono text-[#39FF14] text-sm font-bold">// NEW LOOK AVAILABLE</p>
          <p className="font-mono text-[#A3C7A8] text-xs mt-1 leading-relaxed">
            Try our new Modern theme: brighter cards, metallic tier cards and the full tier showcase.
            {pollOn ? ' Tell us what you think and earn +50 pts.' : ''} You can switch back anytime in Profile → Appearance.
          </p>
          <button type="button" onClick={tryModern} disabled={switching}
            className="mt-3 px-4 py-2 bg-[#39FF14] text-[#050003] font-mono text-xs font-bold hover:bg-[#39FF14]/90 disabled:opacity-60"
            data-testid="theme-launch-try-modern">
            {switching ? 'SWITCHING…' : 'TRY MODERN →'}
          </button>
        </div>
      </div>
    </div>
  );
}
